import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select } from './ui/select';
import { AlertCircle, CheckCircle, Save, RotateCcw, User, Globe, Clock, Camera } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { updateUserProfile } from '../services/api';
import { useUser } from '../hooks/useUser';
import AvatarUpload from './AvatarUpload';

export const UserProfileTab: React.FC = () => {
  const { user: userData, loading, updateUser } = useUser();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    timezone: '',
    theme: 'system' as 'light' | 'dark' | 'system',
    language: 'pt' as 'pt' | 'en'
  });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Timezone options
  const timezoneOptions = [
    { value: 'America/Sao_Paulo', label: 'São Paulo (GMT-3)' },
    { value: 'America/New_York', label: 'New York (GMT-5)' },
    { value: 'Europe/London', label: 'London (GMT+0)' },
    { value: 'Europe/Paris', label: 'Paris (GMT+1)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)' },
    { value: 'UTC', label: 'UTC (GMT+0)' }
  ];

  // Helper function to normalize values
  const normalizeValue = (value: string | undefined, defaultValue: string): string => {
    return value || defaultValue;
  };

  useEffect(() => {
    if (userData) {
      setFormData({
        name: normalizeValue(userData.name, ''),
        phone: normalizeValue(userData.phone, ''),
        timezone: normalizeValue(userData.timezone, 'America/Sao_Paulo'),
        theme: normalizeValue(userData.theme, 'system') as 'light' | 'dark' | 'system',
        language: normalizeValue(userData.language, 'pt') as 'pt' | 'en'
      });
    }
  }, [userData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear success message when user starts editing
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate required fields
      if (!formData.name.trim()) {
        setError('Nome é obrigatório');
        return;
      }

      const response = await updateUserProfile(formData);
      
      // Update user data in context
      updateUser(response.data.data.user);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao salvar perfil');
      console.error('Error saving profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => {
    if (!userData) return false;
    
    // Use the same normalization logic for consistent comparison
    const currentValues = {
      name: normalizeValue(userData.name, ''),
      phone: normalizeValue(userData.phone, ''),
      timezone: normalizeValue(userData.timezone, 'America/Sao_Paulo'),
      theme: normalizeValue(userData.theme, 'system'),
      language: normalizeValue(userData.language, 'pt')
    };

    const hasChanged = (
      formData.name !== currentValues.name ||
      formData.phone !== currentValues.phone ||
      formData.timezone !== currentValues.timezone ||
      formData.theme !== currentValues.theme ||
      formData.language !== currentValues.language
    );

    return hasChanged;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RotateCcw className="w-6 h-6 animate-spin text-blue-400" />
        <span className="ml-2 text-foreground-secondary">Carregando...</span>
      </div>
    );
  }

  const handleAvatarChange = (avatarPath: string | null) => {
    // Avatar is already updated in context by AvatarUpload component
  };

  return (
    <div className="space-y-6">
      {/* Avatar Section */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="w-4 h-4" />
            Avatar
          </CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center">
          <AvatarUpload
            currentAvatar={userData?.avatar}
            onAvatarChange={handleAvatarChange}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Personal Information */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Informações Pessoais
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">Nome Completo *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Seu nome completo"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={userData?.email || ''}
                disabled
                className="mt-1 opacity-50"
              />
              <p className="text-xs text-foreground-tertiary mt-1">
                O email não pode ser alterado por motivos de segurança
              </p>
            </div>

            <div>
              <Label htmlFor="phone">Telefone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="(11) 99999-9999"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card variant="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Preferências
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="timezone">Fuso Horário</Label>
              <Select 
                value={formData.timezone} 
                onChange={(value) => handleInputChange('timezone', value)}
                options={timezoneOptions}
                placeholder="Selecione o fuso horário"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="theme">Tema</Label>
              <Select 
                value={formData.theme} 
                onChange={(value) => handleInputChange('theme', value as 'light' | 'dark' | 'system')}
                options={[
                  { value: 'system', label: 'Sistema' },
                  { value: 'light', label: 'Claro' },
                  { value: 'dark', label: 'Escuro' }
                ]}
                placeholder="Selecione o tema"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="language">Idioma</Label>
              <Select 
                value={formData.language} 
                onChange={(value) => handleInputChange('language', value as 'pt' | 'en')}
                options={[
                  { value: 'pt', label: 'Português' },
                  { value: 'en', label: 'English' }
                ]}
                placeholder="Selecione o idioma"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Account Information */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Informações da Conta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-foreground-secondary">Conta criada em:</span>
              <span className="block text-foreground-primary">
                {userData?.createdAt ? formatDate(userData.createdAt) : 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-foreground-secondary">Última atualização:</span>
              <span className="block text-foreground-primary">
                {userData?.updatedAt ? formatDate(userData.updatedAt) : 'N/A'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-4">
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges()}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Salvar Alterações
        </Button>

        {saveSuccess && (
          <div className="flex items-center text-green-400 text-sm">
            <CheckCircle className="w-4 h-4 mr-1" />
            Perfil atualizado com sucesso!
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">{error}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileTab;
